import { put } from "redux-saga/effects";
import { Client } from "utils/transport";
import { SOMETHING_WENT_WRONG } from "constants/staticText";
import dayjs from "dayjs";
import { hms } from "utils/timeConverter";
import { IntegrationDashboardAction } from "../action";
import { ExportCSV } from "utils/export-csv";
import { getDownloadTableViewColumns } from "utils/commonUtils";

const ExportData = (
  fileName,
  data,
  columns,
  filterDate,
  teams,
  viewFields,
  user_map_data,
  team_list_data
) => {
  let result = [];
  const Generated = "Generated Date:-" + dayjs().format("DD-MM-YYYY hh:mm a");
  const generatedData = [
    fileName,
    teams.length > 0
      ? "Teams: - " + teams.join(",").replaceAll(",", "/")
      : "Teams: - " + "All Teams",
    "Date:-" + filterDate,
    Generated,
    columns.join(),
  ];

  data.forEach((record) => {
    const firstName = record?.first_name || "";
    const lastName = record?.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim() || "--";

    // Get user data for dynamic columns
    const user = user_map_data?.[record.identity_id];

    // Get dynamic column values
    const dynamicValues = getDownloadTableViewColumns(
      viewFields,
      user,
      team_list_data
    );

    // Format akumin_total_time (hours to HH:MM)
    const formatHours = (hours) => {
      if (hours === null || hours === undefined) return "--";
      const h = Math.floor(hours);
      const m = Math.floor((hours - h) * 60);
      return `${h}:${m.toString().padStart(2, "0")}`;
    };

    const fixedValues = [
      formatHours(record?.akumin_total_time),
      record?.calculated_quantity_unique_days !== null &&
      record?.calculated_quantity_unique_days !== undefined
        ? record.calculated_quantity_unique_days.toString()
        : "--",
      formatHours(record?.total_time_off_quantity),
      record?.time_off_unique_days !== null &&
      record?.time_off_unique_days !== undefined
        ? record.time_off_unique_days.toString()
        : "--",

      record?.online_duration ? hms(record.online_duration) : "--",
      record?.active_duration ? hms(record.active_duration) : "--",
      record?.productive_duration ? hms(record.productive_duration) : "--",
      record?.akumin_total_time &&
      record?.online_duration !== null &&
      record?.online_time_percent_vs_calculated !== undefined
        ? `${parseFloat(record.online_time_percent_vs_calculated).toFixed(2)}%`
        : "--",
      record?.akumin_total_time &&
      record?.active_duration !== null &&
      record?.active_time_percent_vs_calculated !== undefined
        ? `${parseFloat(record.active_time_percent_vs_calculated).toFixed(2)}%`
        : "--",
      record?.akumin_total_time &&
      record?.productive_duration !== null &&
      record?.productive_time_percent_vs_calculated !== undefined
        ? `${parseFloat(record.productive_time_percent_vs_calculated).toFixed(
            2
          )}%`
        : "--",

      record?.active_duration &&
      record?.online_duration &&
      record?.active_time_percent !== null &&
      record?.active_time_percent !== undefined
        ? `${parseFloat(record.active_time_percent).toFixed(2)}%`
        : "--",
      record?.productive_duration &&
      record?.online_duration &&
      record?.productive_time_percent !== null &&
      record?.productive_time_percent !== undefined
        ? `${parseFloat(record.productive_time_percent).toFixed(2)}%`
        : "--",
      record?.remark_online_time || "--",
      record?.remark || "--",
      record?.remark_productive_time || "--",
    ];

    generatedData.push([fullName, ...dynamicValues, ...fixedValues].join(","));
  });
  result = generatedData;
  return result;
};

const getDownloadCSV = (action, data) => {
  const teamIds = action.payload.body.group_id?.split(",") || [];
  const selectTeamNames =
    action.payload.team_list_data
      ?.filter((item) => teamIds.includes(item.id))
      .map((item) => item.name) || [];

  const date =
    dayjs(action?.payload.body.start_date).format("YYYY-MM-DD") +
    " To " +
    dayjs(action?.payload.body.end_date).format("YYYY-MM-DD");

  // Dynamic columns from viewFields
  const dynamicFields = action.payload.viewFields || [];
  const dynamicHeaders = dynamicFields.map((field) => field.label);

  // Fixed headers
  const fixedHeaders = [
    "Workday Total Time (Calcualted Quantity)",
    "Calculated Quantity Unique Days",
    "Time Off Quantity",
    "Time Off Unique Days",
    "Online Time",
    "Active Time",
    "Productive Time",
    "Online Time % (compared to Workday Total Time)",
    "Active Time % (compared to Workday Total Time)",
    "Productive Time % (compared to Workday Total Time)",
    "Active Time % (compared to We360 Online Time)",
    "Productive Time % (compared to We360 Online Time)",
    "Remark (Online Time)",
    "Remark (Active Time)",
    "Remark (Productive Time)",
  ];

  // Combine headers: Name, Dynamic columns, Fixed columns
  const finalHeaders = ["Name", ...dynamicHeaders, ...fixedHeaders];

  const exdata = ExportData(
    "Integration Dashboard Report",
    data,
    finalHeaders,
    date,
    selectTeamNames,
    action.payload.viewFields,
    action.payload.user_map_data,
    action.payload.team_list_data
  );

  ExportCSV("Integration_Dashboard_Report_", exdata);
};

export function* fetchIntegrationDashboardSaga(action) {
  const config = {
    method: "POST",
    url: `query/reports/integration_dashboard`,
    headers: {
      ...action.payload.headers,
    },
    data: { ...action.payload.body },
  };

  try {
    const response = yield Client.request(config);
    yield put(
      IntegrationDashboardAction.fetchIntegrationDashboardSuccess(response.data)
    );
  } catch (err) {
    let errorMessage = SOMETHING_WENT_WRONG;
    if (err.response?.status < 500) {
      errorMessage = err.response?.data?.errors?.[0]?.detail;
    }
    yield put(
      IntegrationDashboardAction.fetchIntegrationDashboardFailed(errorMessage)
    );
  }
}

export function* downloadIntegrationDashboardSaga(action) {
  const config = {
    method: "POST",
    url: `query/reports/integration_dashboard`,
    headers: {
      ...action.payload.headers,
    },
    data: { ...action.payload.body },
  };

  try {
    const response = yield Client.request(config);
    const rows = response.data.data || [];
    // Handle nested pagination structure
    const apiPagination =
      response.data.optional?.pagination || response.data.pagination || {};
    const totalRecords = apiPagination.total_records || 0;
    const limit = action.payload.body.limit || 15;
    const totalPages = Math.ceil(totalRecords / limit);

    // Fetch remaining pages if needed
    for (let i = 2; i <= totalPages; i++) {
      const config = {
        method: "POST",
        url: `query/reports/integration_dashboard`,
        headers: {
          ...action.payload.headers,
        },
        data: { ...action.payload.body, page: i },
      };
      const response = yield Client.request(config);
      rows.push(...(response.data.data || []));
    }
    getDownloadCSV(action, rows);
    yield put(
      IntegrationDashboardAction.downloadIntegrationDashboardSuccess(
        response.data
      )
    );
  } catch (err) {
    console.log(err);
    let errorMessage = SOMETHING_WENT_WRONG;
    if (err.response?.status < 500) {
      errorMessage = err.response?.data?.errors?.[0]?.detail;
    }
    yield put(
      IntegrationDashboardAction.downloadIntegrationDashboardFailed(
        errorMessage
      )
    );
  }
}

export function* fetchIntegrationDashboardSummarySaga(action) {
  const config = {
    method: "POST",
    url: `query/reports/integration_dashboard/summary`,
    headers: {
      ...action.payload.headers,
    },
    data: { ...action.payload.body },
  };

  try {
    const response = yield Client.request(config);
    // API response structure: { status, status_code, data: { remark_counts, total_count, actionable_items }, optional }
    // Pass the entire response.data to maintain structure
    yield put(
      IntegrationDashboardAction.fetchIntegrationDashboardSummarySuccess(
        response.data
      )
    );
  } catch (err) {
    let errorMessage = SOMETHING_WENT_WRONG;
    if (err.response?.status < 500) {
      errorMessage = err.response?.data?.errors?.[0]?.detail;
    }
    yield put(
      IntegrationDashboardAction.fetchIntegrationDashboardSummaryFailed(
        errorMessage
      )
    );
  }
}
